import { PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Space, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import VSpace from '../../../common/VSpace'
import CreateRunModal from './CreateRunModal'
import RunDetailModal from './RunDetailModal'
import {
  EvalRunSummary,
  formatPassCount,
  getDefaultRunFilters,
  RunFilters,
  runStatusColor,
} from './runsAdminModel'
import { useRunsListQuery } from './runsAdminQueries'

const RunsExplorer = () => {
  const [filters, setFilters] = useState<RunFilters>(() => getDefaultRunFilters())
  const [creating, setCreating] = useState(false)
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const { data, isLoading } = useRunsListQuery(filters)

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (val: string) => <Tag color={runStatusColor(val)}>{val}</Tag>,
    },
    {
      title: 'Result',
      dataIndex: 'has_failures',
      width: 130,
      render: (hasFailures: boolean) =>
        hasFailures ? (
          <Tag color="red">Has failures</Tag>
        ) : (
          <Tag color="green">All passed</Tag>
        ),
    },
    { title: 'Model', dataIndex: 'model', ellipsis: true },
    {
      title: 'Progress',
      key: 'progress',
      width: 110,
      render: (_: unknown, run: EvalRunSummary) =>
        `${run.completed_trials}/${run.planned_trials}`,
    },
    {
      title: 'Passed',
      key: 'passed',
      width: 100,
      render: (_: unknown, run: EvalRunSummary) => formatPassCount(run),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 200,
      // The list is server-ordered newest-first.
      render: (val: string) => dayjs(val).format('MMM D, YYYY h:mm A'),
    },
  ]

  return (
    <VSpace>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Runs
        </Typography.Title>
        <Space size="large">
          <Checkbox
            checked={filters.hasFailures ?? false}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                hasFailures: e.target.checked || undefined,
                page: 1,
              }))
            }
          >
            Only runs with failures
          </Checkbox>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreating(true)}
          >
            New run
          </Button>
        </Space>
      </div>
      <Table<EvalRunSummary>
        dataSource={data?.runs}
        loading={isLoading}
        rowKey="run_id"
        columns={columns}
        onRow={(record) => ({
          onClick: () => setSelectedRunId(record.run_id),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: 'No runs' }}
        pagination={{
          current: filters.page,
          pageSize: filters.pageSize,
          total: data?.total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total) => `${total} run(s)`,
          onChange: (page) => setFilters((prev) => ({ ...prev, page })),
        }}
      />
      {creating && <CreateRunModal open onClose={() => setCreating(false)} />}
      <RunDetailModal
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </VSpace>
  )
}

export default RunsExplorer
